/**
 * Voluum GET payloads sometimes omit `path_PathUpdateResource` fields that PUT
 * requires. Fill only `undefined` keys with safe defaults before validation / forward.
 */

import { isPlainObject } from './primitives';

function applyBaseDefaults(path: Record<string, unknown>): void {
  if (path.autoOptimized === undefined) path.autoOptimized = false;
  if (path.listicle === undefined) path.listicle = false;
  if (path.offerRedirectMode === undefined) path.offerRedirectMode = 'REGULAR';
  if (path.realtimeRoutingApiState === undefined) {
    path.realtimeRoutingApiState = 'DISABLED';
  }
  if (path.active === undefined) path.active = true;
  if (path.weight === undefined) path.weight = 100;
  if (path.name === undefined) path.name = '';
  if (path.offers === undefined) path.offers = [];
}

/**
 * Voluum contract:
 *  - `autoOptimized: true`  → `autoOptimizationMode` + `calculationMethod` required.
 *  - `autoOptimized: false` → both fields MUST be absent (otherwise Voluum returns
 *    "Improper automation configuration").
 */
function reconcileAutomationFields(path: Record<string, unknown>): void {
  if (path.autoOptimized === true) {
    if (path.autoOptimizationMode === undefined) {
      path.autoOptimizationMode = 'INDEPENDENT';
    }
    if (path.calculationMethod === undefined) path.calculationMethod = 'AUTO';
    return;
  }
  delete path.autoOptimizationMode;
  delete path.calculationMethod;
}

function applyPathDefaults(path: Record<string, unknown>): void {
  applyBaseDefaults(path);
  reconcileAutomationFields(path);
}

function walkPathArray(paths: unknown): void {
  if (!Array.isArray(paths)) return;
  for (const p of paths) {
    if (!isPlainObject(p)) continue;
    applyPathDefaults(p);
  }
}

/** Mutates a flow-like object in place (default paths + group paths). */
export function normalizeVoluumFlowPathsForPut(flow: unknown): void {
  if (!isPlainObject(flow)) return;
  walkPathArray(flow.defaultPaths);
  const groups = flow.conditionalPathsGroups;
  if (!Array.isArray(groups)) return;
  for (const g of groups) {
    if (!isPlainObject(g)) continue;
    walkPathArray(g.paths);
  }
}
